import type { ReactNode } from 'react';
import { matchSheetKind, sheetKind } from '@erp/workbooks';
import type {
  WorkbookSource,
  WorkbookTable as TableData,
} from '@erp/shared-types';
import { sheetSourceTypes } from './workbookCatalog';
import type { WorkbookEditor } from './useWorkbookEditor';
import { AddWorksheetPanel } from './AddWorksheetPanel';
import { FilePanel, SourcePanel } from './WorkbookInputs';
import { AddInputPanel } from './WorkbookAddDataPanel';
import { WorksheetPanel } from './WorksheetPanel';
import { AddPivotPanel } from './pivot/AddPivotPanel';
import { PivotPanel } from './pivot/PivotPanel';
import { FreeformPanel } from './freeform/FreeformPanel';
import { removeWorkbookItem } from './workbookAgentEdits';
import { WorkbookFileOutput } from './WorkbookFileOutput';
import { WorkbookAppOutput } from './WorkbookAppOutput';
import { WorkbookSinkOutput } from './WorkbookSinkOutput';
import { WorkbookOutputResult } from './WorkbookOutputCell';
import { WorkbookReport } from './WorkbookReport';
import { WorkbookRunHistory } from './WorkbookRunHistory';
import { WorkbookAutomations } from './integrations/WorkbookAutomations';
import {
  WorkbookProcessStepActions,
  WorkbookRunActions,
} from './integrations/workbookIntegrations';
import { WorkbookSettings } from './WorkbookSettings';
import { WorkbookVersions } from './WorkbookVersions';
import { WorkbookProcess } from './WorkbookProcessPanel';
import { useWorkbookRunResults } from './workbookRunResults';
import { useWorksheetResult } from './useWorksheetResult';
import { WorkbookRunMenu } from './WorkbookRunMenu';
import { WorkbookItemInstructions } from './WorkbookItemInstructions';
import { useLoadedSourcePreviews } from './useWorkbookPlan';
import { useSchemaGroupReadings } from './useSchemaGroupReadings';
import type { WorkbookDataContext } from './workbookDataItems';
import { WorkbookSheetChoicesProvider } from './workbookSheetChoices';

// Inherited complexity: this branch only wraps the content in WorkbookSheetChoicesProvider (no branches added).
// fallow-ignore-next-line complexity
export function WorkbookContent({
  editor,
  selected,
  active,
  busy,
  canUpdate,
  canExecute,
  act,
  addSource,
  addSheet,
  addPivot,
  openItem,
  uploadFiles,
}: {
  openItem: (id: string) => void;
  /** Uploads spreadsheet files as the Workbook's Files; absent, nothing is uploaded here. */
  uploadFiles?: (files: readonly File[]) => Promise<void>;
  editor: WorkbookEditor;
  selected: string;
  /** This tab is the one on screen: only it keeps a Live preview. */
  active: boolean;
  busy: boolean;
  canUpdate: boolean;
  canExecute: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
  addSource: (source: WorkbookSource) => boolean;
  addSheet: (input: { id: string; name: string }, table: TableData) => void;
  /** Adds a pivot Worksheet summarizing a Source or Worksheet. */
  addPivot: (input: { id: string; name: string }) => void;
}) {
  const { definition } = editor;
  const sheet = definition.sheets.find((item) => item.id === selected);
  const source = definition.sources.find((item) => item.id === selected);
  const file = definition.files.find((item) => item.id === selected);
  const output = definition.outputs.find((item) => item.id === selected);
  const item = sheet ?? source ?? file ?? output;
  const runResults = useWorkbookRunResults(editor);
  const sourcePreviews = useLoadedSourcePreviews(editor.workbookId, definition);
  const schemaGroupReading = useSchemaGroupReadings(
    definition.sources,
    editor.catalog.data ?? [],
  );
  const data: WorkbookDataContext = {
    catalogName: (id) =>
      editor.catalog.data?.find((entry) => entry.id === id)?.name,
    schemaGroupReading,
    preview: (id) => sourcePreviews.get(id),
  };
  const shown = useWorksheetResult(
    editor,
    runResults,
    sheet?.id ?? null,
    active && canExecute,
  );
  const editable = canUpdate && !busy;
  const stepActions = (stepId: string) => (
    <WorkbookProcessStepActions
      workbookId={editor.workbookId}
      stepId={stepId}
      disabled={busy}
    />
  );
  const runMenu: RunMenu = (targetId, isOutput = false) =>
    canExecute ? (
      <WorkbookRunMenu
        editor={editor}
        targetId={targetId}
        output={isOutput}
        disabled={busy}
        act={act}
      />
    ) : undefined;
  return (
    <WorkbookSheetChoicesProvider sheets={definition.sheets}>
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
        {selected === 'process' && (
          // react-doctor-disable-next-line react-doctor/no-render-prop-children -- the Process view's per-node slots predate this change; this PR removes one
          <WorkbookProcess
            definition={definition}
            openItem={openItem}
            canUpdate={editable}
            edits={editor.edits}
            workbookId={editor.workbookId}
            data={data}
            renderRunMenu={(node) => runMenu(node.itemId)}
            renderStepActions={stepActions}
            renderOutputRunMenu={(item) => runMenu(item.id, true)}
            renderOutputResult={(item) => (
              <WorkbookOutputResult
                editor={editor}
                output={item}
                busy={busy}
                act={act}
              />
            )}
          />
        )}
        {item && (
          <WorkbookItemInstructions
            // The Item's own panel below is keyed by its id too.
            key={`instructions-${item.id}`}
            editor={editor}
            itemId={item.id}
            canUpdate={editable}
            active={active}
            openItem={openItem}
            renderStepActions={stepActions}
          />
        )}
        <WorkbookAddTabs
          selected={selected}
          editor={editor}
          addSource={addSource}
          addSheet={addSheet}
          addPivot={addPivot}
          openItem={openItem}
          uploadFiles={editable ? uploadFiles : undefined}
        />
        {file && (
          <FilePanel
            key={file.id}
            editor={editor}
            fileId={file.id}
            onSource={addSource}
          />
        )}
        {source && (
          <SourcePanel
            key={source.id}
            editor={editor}
            source={source}
            openItem={openItem}
            addSheet={addSheet}
            busy={busy}
          />
        )}
        {sheet && (
          <WorkbookSheetPanel
            editor={editor}
            sheet={sheet}
            shown={shown}
            resultOf={(id) => runResults.sheet(id).result}
            runMenu={runMenu(sheet.id)}
            readOnly={!editable}
            openItem={openItem}
            addSheet={addSheet}
            addPivot={addPivot}
          />
        )}
        {output && (
          <WorkbookOutputPanel
            editor={editor}
            output={output}
            busy={busy}
            canUpdate={editable}
            active={active}
            act={act}
            runMenu={runMenu(output.id, true)}
          />
        )}
        <WorkbookIntegrationTabs
          selected={selected}
          editor={editor}
          canUpdate={editable}
        />
        {selected === 'settings' && (
          <WorkbookSettings editor={editor} canUpdate={canUpdate} busy={busy} />
        )}
        {selected === 'versions' && (
          <WorkbookVersions
            editor={editor}
            busy={busy}
            canUpdate={canUpdate}
            act={act}
          />
        )}
      </div>
    </WorkbookSheetChoicesProvider>
  );
}

type RunMenu = (targetId: string, isOutput?: boolean) => ReactNode;
type Sheet = WorkbookEditor['definition']['sheets'][number];
type Output = WorkbookEditor['definition']['outputs'][number];

/** The tabs that add a Source, a Worksheet or a pivot Worksheet. */
function WorkbookAddTabs({
  selected,
  editor,
  addSource,
  addSheet,
  addPivot,
  openItem,
  uploadFiles,
}: {
  selected: string;
  editor: WorkbookEditor;
  addSource: (source: WorkbookSource) => boolean;
  addSheet: (input: { id: string; name: string }, table: TableData) => void;
  addPivot: (input: { id: string; name: string }) => void;
  openItem: (id: string) => void;
  uploadFiles?: (files: readonly File[]) => Promise<void>;
}) {
  if (selected === 'add-input')
    return (
      <AddInputPanel
        editor={editor}
        onSource={addSource}
        uploadFiles={uploadFiles}
      />
    );
  if (selected === 'add-worksheet')
    return (
      <AddWorksheetPanel
        editor={editor}
        addSheet={addSheet}
        openItem={openItem}
      />
    );
  if (selected === 'add-pivot')
    return <AddPivotPanel definition={editor.definition} addPivot={addPivot} />;
  return null;
}

/** A pivot or columnar Worksheet's panel. */
function WorkbookSheetPanel({
  editor,
  sheet,
  shown,
  resultOf,
  runMenu,
  readOnly,
  openItem,
  addSheet,
  addPivot,
}: {
  editor: WorkbookEditor;
  sheet: Sheet;
  shown: ReturnType<typeof useWorksheetResult>;
  /** A Worksheet's shown Result, for the input it reads. */
  resultOf: (
    id: string,
  ) => ReturnType<ReturnType<typeof useWorkbookRunResults>['sheet']>['result'];
  runMenu: ReactNode;
  readOnly: boolean;
  openItem: (id: string) => void;
  addSheet: (input: { id: string; name: string }, table: TableData) => void;
  addPivot: (input: { id: string; name: string }) => void;
}) {
  const { definition } = editor;
  const input = sheetKind(sheet).input();
  const common = {
    workbookId: editor.workbookId,
    definition,
    plan: editor.plan,
    result: shown.result,
    runId: shown.runId,
    inputResult: input === undefined ? undefined : resultOf(input),
    stale: shown.stale,
    ranAt: shown.ranAt,
    preview: shown.preview,
    needsRunHint: shown.needsRunHint,
    runMenu,
    readOnly,
  };
  return matchSheetKind(sheet, {
    columnar: (columnar) => (
      <WorksheetPanel
        {...common}
        openItem={openItem}
        sheet={columnar}
        change={editor.edits.columnarSheet(columnar.id).update}
        sourceTypes={sheetSourceTypes(
          definition,
          columnar,
          editor.catalog.data,
        )}
        onNext={addSheet}
        onPivot={addPivot}
      />
    ),
    pivot: (pivot) => (
      <PivotPanel
        key={pivot.id}
        {...common}
        sheet={pivot}
        change={editor.edits.pivotSheet(pivot.id).update}
      />
    ),
    freeform: (freeform) => (
      <FreeformPanel
        key={freeform.id}
        {...common}
        sheet={freeform}
        change={editor.edits.freeformSheet(freeform.id).update}
        onDelete={() =>
          editor.edits.workbook.update((latest) =>
            removeWorkbookItem(latest, 'sheets', freeform.id),
          )
        }
      />
    ),
  });
}

/** An Output's panel: a File's, a Report's, a Data sink's or an App's. */
function WorkbookOutputPanel({
  editor,
  output,
  busy,
  canUpdate,
  active,
  act,
  runMenu,
}: {
  editor: WorkbookEditor;
  output: Output;
  busy: boolean;
  canUpdate: boolean;
  active: boolean;
  act: (operation: () => Promise<unknown>) => Promise<void>;
  runMenu: ReactNode;
}) {
  if (output.kind === 'file')
    return (
      <WorkbookFileOutput
        editor={editor}
        output={output}
        busy={busy}
        act={act}
        runMenu={runMenu}
      />
    );
  if (output.kind === 'app')
    return (
      <WorkbookAppOutput
        workbookId={editor.workbookId}
        output={output}
        canUpdate={canUpdate}
      />
    );
  if (output.kind === 'sink')
    return (
      <WorkbookSinkOutput
        editor={editor}
        output={output}
        busy={busy}
        runMenu={runMenu}
      />
    );
  return (
    <WorkbookReport
      editor={editor}
      output={output}
      canUpdate={canUpdate}
      runMenu={runMenu}
      active={active}
    />
  );
}

/** The Runs tab, with each integration's Run actions, and the Automations tab. */
function WorkbookIntegrationTabs({
  selected,
  editor,
  canUpdate,
}: {
  selected: string;
  editor: WorkbookEditor;
  canUpdate: boolean;
}) {
  if (selected === 'runs')
    return (
      <WorkbookRunHistory
        editor={editor}
        renderActions={(run) => (
          <WorkbookRunActions editor={editor} run={run} />
        )}
      />
    );
  if (selected === 'automations')
    return <WorkbookAutomations editor={editor} canUpdate={canUpdate} />;
  return null;
}
